import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { cn } from '../utils/className';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  className?: string;
  'aria-label': string; // Required for accessibility since there's no visible text
  iconSize?: 'sm' | 'md' | 'lg'; // Icon size variant
  shape?: 'circle' | 'square';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      className,
      'aria-label': ariaLabel,
      iconSize = 'md',
      shape = 'circle',
      ...props
    },
    ref
  ) => {
    const iconSizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
    };

    return (
      <button
        ref={ref}
        aria-label={ariaLabel}
        className={cn(
          // Base styles for flat icon button
          'inline-flex items-center justify-center',
          shape === 'square'
            ? 'h-11 w-11 rounded-lg'
            : 'h-10 w-10 rounded-full',
          'text-primary',
          'transition-all duration-150 ease-in-out',
          'cursor-pointer',
          'hover:bg-surface-focus-support',
          'active:brightness-95',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'disabled:hover:bg-transparent disabled:hover:opacity-50',
          className
        )}
        {...props}
      >
        <span
          className={cn(
            'flex items-center justify-center [&>svg]:h-full [&>svg]:w-full',
            iconSizeClasses[iconSize]
          )}
        >
          {icon}
        </span>
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
